import { css } from '@emotion/react';
import { Button, color, spacing, typography } from '@uniquegood/realworld-studio-design';

import ChannelSelectorItem from '../components/ChannelSelectorItem';
import ConnectCard from '../components/ConnectCard';
import Countdown from '../components/Countdown';
import UserInfoCard from '../components/UserInfoCard';
import { ChannelOption } from '../types';

interface ReadyStateProps {
  email: string;
  nickname: string;
  remainingSeconds: number | null;
  channels: ChannelOption[];
  selectedChannelId: string | null;
  onSelectChannel: (channelId: string) => void;
  onConnect: () => void;
}

/**
 * success-ready state.
 *
 * exchange + 로그인 + 채널 보유 모두 충족된 상태.
 * 사용자가 채널을 선택하고 "연결하기" 버튼을 누르면 학교 콜백 URL로 redirect 한다.
 *
 * 단일 채널이어도 selector 카드로 명시 표시한다 (디자인 확정 사항).
 */
export default function ReadyState({
  email,
  nickname,
  remainingSeconds,
  channels,
  selectedChannelId,
  onSelectChannel,
  onConnect
}: ReadyStateProps) {
  const isConnectDisabled = !selectedChannelId;

  return (
    <ConnectCard eyebrow="Realworld School" title="스쿨 계정 연결">
      <div css={styles.body} data-testid="connect-state-ready">
        <p css={styles.description}>연결할 채널을 선택해 주세요.</p>

        <UserInfoCard email={email} nickname={nickname} maskEmail />

        <div css={styles.channelList} data-testid="connect-channel-list">
          {channels.map((channel) => (
            <ChannelSelectorItem
              key={channel.id}
              channel={channel}
              isSelected={selectedChannelId === channel.id}
              onSelect={onSelectChannel}
            />
          ))}
        </div>

        <div css={styles.footer}>
          <Countdown remainingSeconds={remainingSeconds} />
          <Button
            type="primary"
            size="medium"
            onClick={onConnect}
            disabled={isConnectDisabled}
            cssStyle={styles.button}
          >
            연결하기
          </Button>
        </div>
      </div>
    </ConnectCard>
  );
}

const styles = {
  body: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.medium};
  `,
  description: css`
    margin: 0;
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    line-height: 1.5;
  `,
  channelList: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.small};
    max-height: 240px;
    overflow-y: auto;
  `,
  footer: css`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.small};
  `,
  button: css`
    width: 100%;
    font-weight: ${typography.weight.medium};
  `
};
